// Logger utility for consistent logging across the application
// This allows us to easily enable/disable logging in different environments

// Configuration
const config = {
  // Set to true to enable logging in all environments (including production)
  FORCE_DEBUG: false,
  
  // Set to true for more verbose logging
  VERBOSE_MODE: false,
  
  // Set to true to log to localStorage (useful for debugging in production)
  LOG_TO_STORAGE: false,
  
  // Maximum number of log entries to keep in localStorage
  MAX_LOG_ENTRIES: 100,
  
  // Log level thresholds
  levels: {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3
  },
  
  // Current log level (logs below this level will not be shown)
  // 0 = show all logs, 3 = show only errors
  currentLevel: 0
};

// Initialize log storage
const initLogStorage = () => {
  if (config.LOG_TO_STORAGE) {
    try {
      if (!localStorage.getItem('appLogs')) {
        localStorage.setItem('appLogs', JSON.stringify([]));
      }
    } catch (e) {
      // Fail silently if localStorage is not available
    }
  }
};

// Add a log entry to localStorage
const addLogToStorage = (level, ...args) => {
  if (!config.LOG_TO_STORAGE) return;
  
  try {
    const logs = JSON.parse(localStorage.getItem('appLogs') || '[]');
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ')
    };
    
    logs.push(logEntry);
    
    // Keep only the most recent logs
    while (logs.length > config.MAX_LOG_ENTRIES) {
      logs.shift();
    }
    
    localStorage.setItem('appLogs', JSON.stringify(logs));
  } catch (e) {
    // Fail silently if localStorage is not available
  }
};

// Clear all logs from storage
const clearLogs = () => {
  try {
    localStorage.setItem('appLogs', JSON.stringify([]));
  } catch (e) {
    // Fail silently if localStorage is not available
  }
};

// Get all logs from storage
const getLogs = () => {
  try {
    return JSON.parse(localStorage.getItem('appLogs') || '[]');
  } catch (e) {
    return [];
  }
};

// Create logger functions
const createLogger = () => {
  const isDebugEnabled = () => {
    return config.FORCE_DEBUG || process.env.NODE_ENV !== 'production';
  };
  
  const shouldLog = (level) => {
    return isDebugEnabled() && config.levels[level] >= config.currentLevel;
  };
  
  const logger = {
    debug: (...args) => {
      if (shouldLog('debug')) {
        console.debug('[DEBUG]', ...args);
        addLogToStorage('debug', ...args);
      }
    },
    
    info: (...args) => {
      if (shouldLog('info')) {
        console.info('[INFO]', ...args);
        addLogToStorage('info', ...args);
      }
    },
    
    warn: (...args) => {
      if (shouldLog('warn')) {
        console.warn('[WARN]', ...args);
        addLogToStorage('warn', ...args);
      }
    },
    
    error: (...args) => {
      if (shouldLog('error')) {
        console.error('[ERROR]', ...args);
        addLogToStorage('error', ...args);
      }
    },
    
    // Utility methods
    clearLogs,
    getLogs,
    
    // Enable/disable debugging at runtime
    enableDebug: () => {
      config.FORCE_DEBUG = true;
      config.currentLevel = 0;
      logger.debug('Debug logging enabled');
    },
    
    disableDebug: () => {
      config.FORCE_DEBUG = false;
      config.currentLevel = 2; // Only show warnings and errors
      logger.warn('Debug logging disabled');
    },
    
    // Toggle verbose mode
    setVerbose: (isVerbose) => {
      config.VERBOSE_MODE = isVerbose;
      logger.debug(`Verbose logging ${isVerbose ? 'enabled' : 'disabled'}`);
    },
    
    // Get current configuration
    getConfig: () => ({...config})
  };
  
  // Initialize storage on creation
  initLogStorage();
  
  return logger;
};

// Export a singleton instance
const logger = createLogger();

// Add a window-level access point for debugging in production
if (typeof window !== 'undefined') {
  window.appLogger = logger;
}

export default logger;